import React, { useState } from 'react';
import { Search, X, Lock, LayoutGrid, GalleryHorizontal, ShoppingCart, BookOpen } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { comics } from '../../data/comics';
import { EncryptedImage } from '../EncryptedImage';
import { useTapGuard } from '../../utils/tap';
import { useHorizontalDragScroll } from '../../hooks/useHorizontalDragScroll';
import { FEATURED_IDS, LIBRARY_FIRST_IDS } from '../../data/monthly';

// How the shelf is laid out: 'grid' (two columns, everything in sight) or 'row' (one
// line to swipe sideways). Remembered between visits.
const VIEW_KEY = 'footparadise_library_view';

export const ComicLibrary = ({
  searchQuery,
  setSearchQuery,
  unlockedComics,
  unlockingComic,
  showUnlockInput,
  onComicClick,
  onUnlockClick,
  onBuyClick,
}) => {
  const { t, language } = useLanguage();
  const guard = useTapGuard();
  const scrollRef = useHorizontalDragScroll();
  const [view, setView] = useState(() => (localStorage.getItem(VIEW_KEY) === 'row' ? 'row' : 'grid'));

  const changeView = (next) => {
    setView(next);
    localStorage.setItem(VIEW_KEY, next);
  };

  // The order comes from data/monthly.js: the listed comics first, then the rest from
  // newest to oldest. The comic of the month is not in the library.
  const getOrderedComics = () => {
    const first = LIBRARY_FIRST_IDS.map(id => comics.find(c => c.id === id));
    const excludedIds = [...LIBRARY_FIRST_IDS, ...FEATURED_IDS];
    const otherComics = comics.filter(c => !excludedIds.includes(c.id)).reverse();
    return [...first, ...otherComics].filter(Boolean);
  };

  const orderedComics = getOrderedComics();
  const filteredComics = orderedComics.filter(comic =>
    comic.title.toLowerCase().includes(searchQuery.toLowerCase())
  );
  // a search always shows its results as a grid: nothing hidden off to the side
  const asGrid = view === 'grid' || searchQuery !== '';

  return (
    <section style={styles.librarySection}>
      <div style={styles.libraryHeader}>
        <div style={styles.titleRow}>
          <h3 className="fp-ribbon" style={styles.libraryTitle}>{t('comicLibrary')}</h3>
          <span className="fp-chip fp-chip--dark">{orderedComics.length}</span>
        </div>
        <p style={styles.libraryDescription}>{t('browseCollection')}</p>

        <div style={styles.toolsRow}>
          {/* Search Bar */}
          <div style={styles.searchContainer}>
            <Search size={18} color="#6b4a9a" style={styles.searchIcon} />
            <input
              type="text"
              placeholder={t('searchComics')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
              data-testid="search-comics-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={styles.clearSearchButton}
                data-testid="clear-search-btn"
              >
                <X size={16} color="#6b4a9a" />
              </button>
            )}
          </div>

          <button
            className="fp-btn fp-btn--sm fp-btn--violet"
            style={styles.viewButton}
            onClick={guard(() => changeView(view === 'grid' ? 'row' : 'grid'))}
            aria-label={view === 'grid' ? 'Row view' : 'Grid view'}
            data-testid="library-view-btn"
          >
            {view === 'grid' ? <GalleryHorizontal size={20} strokeWidth={2.6} /> : <LayoutGrid size={20} strokeWidth={2.6} />}
          </button>
        </div>
      </div>

      {filteredComics.length === 0 && (
        <p className="fp-msg fp-msg--warn" style={styles.empty}>
          {language === 'es' ? 'No hay cómics con ese nombre.' : 'No comics with that name.'}
        </p>
      )}

      <div ref={scrollRef} className={asGrid ? undefined : 'fp-noscrollbar'} style={asGrid ? styles.grid : styles.row}>
        {filteredComics.map((comic) => {
          const isLocked = comic.locked && !unlockedComics.includes(comic.id);
          const isUnlocking = unlockingComic === comic.id;
          const isUnlocked = unlockedComics.includes(comic.id);

          return (
            <div
              key={comic.id}
              className="fp-card"
              style={asGrid ? styles.comicCard : styles.comicCardRow}
              data-testid={`comic-card-${comic.id}`}
            >
              <div
                className="fp-cover"
                style={{
                  ...styles.coverContainer,
                  cursor: isLocked ? 'default' : 'pointer',
                  ...(isUnlocking ? styles.unlockingCard : {}),
                }}
                onClick={() => onComicClick(comic.id)}
              >
                <EncryptedImage
                  src={comic.coverUrl}
                  alt={comic.title}
                  style={styles.comicCover}
                />

                {/* Unlock Animation Overlay */}
                {isUnlocking && (
                  <div style={styles.unlockAnimationOverlay}>
                    <div style={styles.unlockFlash}></div>
                    <div style={styles.unlockSparkles}>
                      {[...Array(8)].map((_, i) => (
                        <div
                          key={i}
                          style={{
                            ...styles.unlockSparkle,
                            animationDelay: `${i * 0.1}s`,
                            left: `${15 + (i % 3) * 30}%`,
                            top: `${15 + Math.floor(i / 3) * 25}%`,
                          }}
                        >✨</div>
                      ))}
                    </div>
                    <div style={styles.unlockTextSmall}>{t('unlocked')}</div>
                  </div>
                )}

                {isLocked && !isUnlocking && <div style={styles.lockedVeil} />}
                {isLocked && showUnlockInput !== comic.id && !isUnlocking && (
                  <button
                    className="fp-btn fp-btn--sm"
                    style={styles.unlockButton}
                    onClick={(e) => onUnlockClick(comic.id, e)}
                    data-testid={`unlock-btn-${comic.id}`}
                  >
                    <Lock size={13} strokeWidth={3} />
                    {t('unlock')}
                  </button>
                )}
                {comic.id === 2 && <span className="fp-chip fp-chip--lime" style={styles.cornerChip}>{t('free')}</span>}
              </div>

              <h3 style={styles.comicTitle}>{comic.title}</h3>

              {comic.id !== 2 && isUnlocked ? (
                <button
                  className="fp-btn fp-btn--sm fp-btn--gold fp-btn--block"
                  onClick={() => onComicClick(comic.id)}
                  data-testid={`read-btn-${comic.id}`}
                >
                  <BookOpen size={15} strokeWidth={3} />
                  {language === 'es' ? 'Leer' : 'Read'}
                </button>
              ) : (
                <button
                  className={`fp-btn fp-btn--sm fp-btn--block ${comic.id === 2 ? 'fp-btn--cyan' : 'fp-btn--green'}`}
                  onClick={(e) => {
                    if (comic.id === 2) {
                      onComicClick(comic.id);
                    } else if (!unlockedComics.includes(comic.id)) {
                      onBuyClick(comic.id, e);
                    }
                  }}
                  disabled={unlockedComics.includes(comic.id) && comic.id !== 2}
                  data-testid={`buy-btn-${comic.id}`}
                >
                  {comic.id === 2 ? <BookOpen size={15} strokeWidth={3} /> : <ShoppingCart size={15} strokeWidth={3} />}
                  {comic.id === 2 ? t('readFree') : t('buy')}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const card = {
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  minWidth: 0,
};

const styles = {
  librarySection: {
    position: 'relative',
    marginTop: '26px',
    padding: '0 0 26px',
    background: 'linear-gradient(180deg, rgba(20,6,31,0.78) 0%, rgba(27,10,51,0.6) 100%)',
    borderTop: '3px solid #000',
    borderBottom: '3px solid #000',
    boxShadow: '0 -3px 0 #ffd400, 0 3px 0 #ffd400',
  },
  libraryHeader: {
    padding: '0 14px 16px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '14px',
    marginTop: '-18px',
  },
  libraryTitle: {
    margin: 0,
    fontSize: '17px',
    whiteSpace: 'nowrap',
  },
  libraryDescription: {
    fontSize: '12.5px',
    lineHeight: 1.4,
    color: '#eadcff',
    margin: '14px 0 0',
    textAlign: 'center',
  },
  toolsRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '10px',
    marginTop: '14px',
  },
  searchContainer: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fff',
    border: '3px solid #000',
    borderRadius: '14px',
    padding: '9px 12px',
    boxShadow: '0 4px 0 #000',
    position: 'relative',
  },
  searchIcon: {
    marginRight: '8px',
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    fontSize: '15px',
    fontWeight: 600,
    color: '#1b0a33',
    backgroundColor: 'transparent',
    padding: '0',
  },
  clearSearchButton: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewButton: {
    padding: '0 12px',
    flexShrink: 0,
  },
  empty: {
    margin: '0 14px 8px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '22px 14px',
    padding: '4px 14px 0',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    gap: '14px',
    padding: '4px 14px 10px',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    scrollSnapType: 'x proximity',
    scrollPaddingLeft: '14px',
  },
  comicCard: card,
  comicCardRow: {
    ...card,
    width: '44vw',
    maxWidth: '190px',
    flexShrink: 0,
    scrollSnapAlign: 'start',
  },
  unlockingCard: {
    animation: 'unlockGlow 1.5s ease-in-out',
    zIndex: 10,
  },
  coverContainer: {
    width: '100%',
  },
  comicCover: {
    width: '100%',
    aspectRatio: '2/3',
    objectFit: 'cover',
    display: 'block',
  },
  lockedVeil: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(20,6,31,0) 35%, rgba(20,6,31,0.72) 100%)',
    pointerEvents: 'none',
  },
  unlockButton: {
    position: 'absolute',
    left: '8px',
    right: '8px',
    bottom: '10px',
    zIndex: 2,
    padding: '8px 6px',
    fontSize: '11.5px',
    letterSpacing: '0.3px',
  },
  cornerChip: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    zIndex: 2,
  },
  comicTitle: {
    margin: '12px 2px 9px',
    fontSize: '13px',
    fontWeight: 800,
    color: '#fff',
    textAlign: 'center',
    height: '33px',
    lineHeight: '1.25',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    textShadow: '0 2px 0 rgba(0,0,0,0.5)',
  },
  // Animation overlays
  unlockAnimationOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  unlockFlash: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.9) 0%, rgba(255, 255, 255, 0.95) 50%, rgba(255, 20, 147, 0.9) 100%)',
    animation: 'flashUnlock 1.5s ease-out forwards',
  },
  unlockSparkles: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  unlockSparkle: {
    position: 'absolute',
    fontSize: '24px',
    animation: 'sparkleUnlock 1s ease-out forwards',
    opacity: 0,
  },
  unlockTextSmall: {
    position: 'relative',
    fontSize: '18px',
    fontWeight: 900,
    fontStyle: 'italic',
    color: '#FFD700',
    textShadow: 'var(--fp-outline)',
    animation: 'textPopIn 0.5s ease-out 0.3s both',
    letterSpacing: '1px',
  },
};

export default ComicLibrary;
